<template>
  <div
    v-if="isVisible"
    id="FitGuide"
    class="modal"
    style="display: block;"
    @click.self="stopVto"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-body">
          <button
            type="button"
            class="btn-default modal_close-btn"
            @click="stopVto"
            @touchend.prevent="stopVto"
          >
            <i class="icon-icon-close" />
          </button>

          <div
            v-if="swatchProducts.length"
            class="swatch_absolute-container"
          >
            <!-- Use keen-slider when more than 5 items -->
            <keen-slider
              v-if="swatchProducts.length > 5"
              class="swatch_container keen-slider-vertical"
              :options="verticalSliderOptions"
              arrows
            >
              <div
                v-for="prod in swatchProducts"
                :key="prod.id"
                class="product-variant_swatch"
                :class="{ current: prod.selectedOrFirstAvailableVariant.sku === currentSku }"
                @click="openFrame(null, prod.selectedOrFirstAvailableVariant.sku)"
              >
                <img
                  :src="prod.images && prod.images.edges && prod.images.edges[0] && prod.images.edges[0].node.url"
                  :alt="prod.title"
                  width="50"
                  height="50"
                  loading="lazy"
                >
              </div>
            </keen-slider>

            <!-- Use regular div when 5 or fewer items -->
            <div
              v-else
              class="swatch_container"
            >
              <div
                v-for="prod in swatchProducts"
                :key="prod.id"
                class="product-variant_swatch"
                :class="{ current: prod.selectedOrFirstAvailableVariant.sku === currentSku }"
                @click="openFrame(null, prod.selectedOrFirstAvailableVariant.sku)"
              >
                <img
                  :src="prod.images && prod.images.edges && prod.images.edges[0] && prod.images.edges[0].node.url"
                  :alt="prod.title"
                  width="50"
                  height="50"
                  loading="lazy"
                >
              </div>
            </div>
          </div>

          <div
            id="my-fitmix-container"
            ref="fitmixContainer"
            class="popup-vto__fitmix-container"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import KeenSlider from './keen-slider.vue'
import { storefrontFetch } from '@/js/utils/storefront'
import { productByIdQuery, groupProductsQuery } from '@/js/utils/data'

const API_KEY = 'o7QocmLH8SpKrQtFpup8RT1i1ymqeOSlWtJatNC9'

export default {
  name: 'PopupVto',

  components: {
    KeenSlider
  },

  data () {
    return {
      isVisible: false,
      currentSku: null,
      widget: null,
      swatchProducts: []
    }
  },

  computed: {
    currentSwatchIndex () {
      if (!this.currentSku || !this.swatchProducts.length) return 0
      const idx = this.swatchProducts.findIndex(
        prod => prod.selectedOrFirstAvailableVariant.sku === this.currentSku
      )
      return idx >= 0 ? idx : 0
    },

    verticalSliderOptions () {
      return {
        slides: {
          perView: 5,
          spacing: 0
        },
        vertical: true,
        mode: 'snap',
        loop: false,
        initial: this.currentSwatchIndex
      }
    }
  },

  mounted () {
    // Listen for global custom event to open VTO
    window.addEventListener('open-vto', this.handleOpenVto)

    // Expose global function for non-Vue callers (Liquid onclick, Boost PFS, etc.)
    window.openVTO = (sku, swatchProducts) => {
      this.open(sku, swatchProducts)
    }

    // Keep backward-compatible global selectFrame function
    window.selectFrame = async (event, sku) => {
      const productId = event.currentTarget?.closest('.ss__result--item')?.id?.replace('ss__result--search-', '')
      document.querySelectorAll('#searchspring-content #FitGuide').forEach(el => el.remove())
      if (productId) {
        this.fetchSwatchProductsFromID(productId)
      }

      this.openFrame(event, sku)
    }

    window.onStopVTO = () => {
      this.stopVto()
    }

    // Process any pending VTO open that was queued before component mounted
    if (window._vtoPendingOpen) {
      const { sku, swatchProducts, element } = window._vtoPendingOpen
      delete window._vtoPendingOpen
      if (element) {
        this.openFrame(element, sku)
      } else {
        this.open(sku, swatchProducts)
      }
    }
  },

  beforeDestroy () {
    window.removeEventListener('open-vto', this.handleOpenVto)
  },

  methods: {
    handleOpenVto (event) {
      const { sku, swatchProducts } = event.detail || {}
      this.open(sku, swatchProducts)
    },

    open (sku, swatchProducts) {
      if (swatchProducts) {
        this.swatchProducts = swatchProducts
      }

      const skuFrame = sku || this.getSkuFromDom()
      if (!skuFrame) return

      this.currentSku = skuFrame
      this.isVisible = true
      document.body.classList.add('modal-open')

      this.$nextTick(() => {
        this.initWidget(skuFrame)
      })
    },

    getSkuFromDom () {
      const el = document.querySelector('[data-sku-frame]')
      return el ? el.getAttribute('data-sku-frame') : null
    },

    initWidget (sku) {
      const container = this.$refs.fitmixContainer
      if (!container) return

      container.style.display = 'block'

      const params = {
        apiKey: API_KEY,
        onStopVto: () => this.hidePopup()
      }

      // eslint-disable-next-line no-undef
      if (!this.widget) {
        this.widget = window.FitMix.createWidget('my-fitmix-container', params, () => {
          this.widget.setFrame(sku)
          this.widget.startVto('live')
        })
      } else {
        this.widget.setFrame(sku)
        this.widget.startVto('live')
      }
    },

    async fetchSwatchProductsFromID (productId) {
      try {
        this.swatchProducts = []
        // 1. Find the product by ID to get its tags
        const data = await storefrontFetch(productByIdQuery, {
          productId: `gid://shopify/Product/${productId}`
        })

        if (!data || !data.product) return

        const product = data.product
        const groupTag = product.tags.find(tag => tag.toLowerCase().startsWith('group:'))
        if (!groupTag) return

        // 2. Fetch all products in the same group
        let cursor = null
        const allProducts = []

        do {
          const data = await storefrontFetch(groupProductsQuery, {
            cursor,
            groupTag: `tag:'${groupTag}'`
          })

          if (!data || !data.products) break

          const edges = data.products.edges
          allProducts.push(...edges.map(e => e.node))

          cursor = data.products.pageInfo.hasNextPage
            ? data.products.pageInfo.endCursor
            : null
        } while (cursor)

        // 3. Filter for exact tag matches only
        this.swatchProducts = allProducts.filter(p =>
          p.tags.some(tag => tag === groupTag)
        )
      } catch (err) {
        console.error('Failed to fetch swatch products from ID:', err)
      }
    },

    openFrame (element, sku) {
      const skuFrame = sku || this.getSkuFromDom()
      if (!skuFrame) return

      this.currentSku = skuFrame

      if (!this.isVisible) {
        this.isVisible = true
        document.body.classList.add('modal-open')

        this.$nextTick(() => {
          this.initWidget(skuFrame)
        })
      } else {
        this.initWidget(skuFrame)
      }
    },

    stopVto () {
      if (this.widget) {
        this.widget.stopVto()
        this.widget = null
      }
      this.hidePopup()
    },

    hidePopup () {
      this.isVisible = false
      document.body.classList.remove('modal-open')

      // Clean up any leftover backdrop from Bootstrap modal
      const modalBackdrop = document.querySelectorAll('.modal-backdrop.in')
      if (modalBackdrop) {
        modalBackdrop.forEach(el => {
          el.remove()
        })
      }
    }
  }
}
</script>
